'use client';
import { useState, useEffect, useRef } from 'react';

import { usePathname, useRouter } from 'next/navigation';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';
import { useTheme } from '@/contexts/ThemeContext';

const serviceLinks = [
  { href: '/services/energy', label: 'Energy' },
  { href: '/services/telecommunications', label: 'Telecommunications' },
  { href: '/services/municipal', label: 'Municipal' },
  // { href: '/services/industrial', label: 'Industrial' }, // hidden — page preserved
];

const navLinks = [
  { href: '/homepage', label: 'Home' },
  { href: '/projects', label: 'Projects' },
  { href: '/contact', label: 'Contact' },
  { href: '/careers', label: 'Careers' },
  { href: '/store/login', label: 'Employee Store' },
];

export default function Navigation() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuClosing, setMenuClosing] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [backdropVisible, setBackdropVisible] = useState(false);
  const pathname = usePathname();
  const router = useRouter();
  const menuRef = useRef<HTMLDivElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const touchStartX = useRef<number>(0);
  const touchStartY = useRef<number>(0);
  const { theme, toggleTheme } = useTheme();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    closeMenu();
  }, [pathname]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      const inDesktop = menuRef.current && menuRef.current.contains(target);
      const inMobile = mobileMenuRef.current && mobileMenuRef.current.contains(target);
      if (!inDesktop && !inMobile) {
        closeMenu();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Show/hide backdrop with slight delay for smooth dimming
  useEffect(() => {
    if (menuOpen && !menuClosing) {
      // Small delay so backdrop fades in after menu starts opening
      const t = setTimeout(() => setBackdropVisible(true), 10);
      return () => clearTimeout(t);
    } else {
      setBackdropVisible(false);
    }
  }, [menuOpen, menuClosing]);

  const closeMenu = () => {
    if (menuOpen) {
      setMenuClosing(true);
      setBackdropVisible(false);
      setTimeout(() => {
        setMenuOpen(false);
        setMenuClosing(false);
        setServicesOpen(false);
      }, 200);
    } else {
      setMenuOpen(false);
      setServicesOpen(false);
    }
  };

  const handleToggleMenu = () => {
    if (menuOpen) {
      closeMenu();
    } else {
      setMenuOpen(true);
      setMenuClosing(false);
    }
  };

  const handleNavClick = (href: string, e: React.MouseEvent) => {
    e.preventDefault();
    closeMenu();
    document.documentElement.classList.add('page-transitioning');
    setTimeout(() => {
      router.push(href);
      setTimeout(() => {
        document.documentElement.classList.remove('page-transitioning');
      }, 300);
    }, 200);
  };

  // Swipe-to-close: detect left or upward swipe on the menu
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    const deltaX = e.changedTouches[0].clientX - touchStartX.current;
    const deltaY = e.changedTouches[0].clientY - touchStartY.current;
    // Swipe left (deltaX < -40) or swipe up (deltaY < -40) closes the menu
    if (deltaX < -40 || deltaY < -40) {
      closeMenu();
    }
  };

  const isActive = (href: string) => pathname === href;
  const isServicesActive = serviceLinks.some(l => pathname === l.href);

  return (
    <>
      {/* Backdrop overlay — dims the page behind the menu */}
      {menuOpen && (
        <div
          onClick={closeMenu}
          className={`fixed inset-0 z-40 bg-black transition-opacity duration-300 ${
            backdropVisible ? 'opacity-40' : 'opacity-0'
          }`}
          aria-hidden="true"
        />
      )}

      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled || menuOpen
            ? 'bg-background/95 backdrop-blur-md border-b border-border shadow-lg'
            : 'bg-gradient-to-b from-background/95 via-background/40 to-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-16">
          <div className="flex items-center h-20 sm:h-24 md:h-32">

            {/* Desktop: Theme Toggle + Hamburger on the RIGHT (hidden on mobile) */}
            <div className="hidden md:flex items-center gap-2 relative md:order-2 md:ml-auto md:-mr-[54px]" ref={menuRef}>
              {/* Theme Toggle */}
              <button
                onClick={toggleTheme}
                className={`p-3 min-w-[44px] min-h-[44px] flex items-center justify-center rounded-lg transition-colors hover:bg-white/20 ${theme === 'light' ? 'text-gray-500' : 'text-white/80'}`}
                aria-label={theme === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}
              >
                {theme === 'light' ? (
                  <Icon name="MoonIcon" size={22} />
                ) : (
                  <Icon name="SunIcon" size={22} />
                )}
              </button>

              <button
                onClick={handleToggleMenu}
                className={`p-3 min-w-[44px] min-h-[44px] flex items-center justify-center rounded-lg transition-colors hover:bg-white/20 ${theme === 'light' ? 'text-gray-500' : 'text-white/80'}`}
                aria-label="Toggle menu"
                aria-expanded={menuOpen}
              >
                <Icon name={menuOpen ? 'XMarkIcon' : 'Bars3Icon'} size={26} />
              </button>

              {/* Dropdown Menu — anchored to right on desktop */}
              {menuOpen && (
                <div
                  onTouchStart={handleTouchStart}
                  onTouchEnd={handleTouchEnd}
                  className={`absolute top-full right-0 mt-2 w-64 bg-card border border-border rounded-xl shadow-2xl overflow-hidden ${
                    menuClosing ? 'animate-menu-slide-up' : 'animate-menu-slide-down'
                  }`}
                >
                  {navLinks.slice(0, 1).map((link, idx) => (
                    <a
                      key={link.href}
                      href={link.href}
                      onClick={(e) => handleNavClick(link.href, e)}
                      style={{ animationDelay: `${idx * 40}ms` }}
                      className={`animate-menu-item flex items-center gap-3 px-5 py-4 text-sm font-medium transition-colors border-b border-border cursor-pointer min-h-[52px] ${
                        isActive(link.href)
                          ? 'text-primary bg-primary/10' : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                      }`}
                    >
                      {link.label}
                    </a>
                  ))}

                  {/* Services with sub-items */}
                  <div style={{ animationDelay: '40ms' }} className="animate-menu-item">
                    <button
                      onClick={() => setServicesOpen(!servicesOpen)}
                      className={`w-full flex items-center justify-between px-5 py-4 text-sm font-medium transition-colors border-b border-border min-h-[52px] ${
                        isServicesActive
                          ? 'text-primary bg-primary/10' : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                      }`}
                    >
                      Services
                      <Icon
                        name="ChevronDownIcon"
                        size={14}
                        className={`transition-transform duration-200 ${servicesOpen ? 'rotate-180' : ''}`}
                      />
                    </button>
                    {servicesOpen && (
                      <div className="bg-muted/50 animate-services-expand">
                        {serviceLinks.map((link, idx) => (
                          <a
                            key={link.href}
                            href={link.href}
                            onClick={(e) => handleNavClick(link.href, e)}
                            style={{ animationDelay: `${idx * 30}ms` }}
                            className={`animate-menu-item flex items-center gap-3 pl-8 pr-5 py-3.5 text-sm transition-colors border-b border-border last:border-0 cursor-pointer min-h-[48px] ${
                              isActive(link.href)
                                ? 'text-primary bg-primary/10' : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                            }`}
                          >
                            <span className="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0" />
                            {link.label}
                          </a>
                        ))}
                      </div>
                    )}
                  </div>

                  {navLinks.slice(1).map((link, i) => (
                    <a
                      key={link.href}
                      href={link.href}
                      onClick={(e) => handleNavClick(link.href, e)}
                      style={{ animationDelay: `${(i + 2) * 40}ms` }}
                      className={`animate-menu-item flex items-center gap-3 px-5 py-4 text-sm font-medium transition-colors cursor-pointer min-h-[52px] ${
                        i < navLinks.slice(1).length - 1 ? 'border-b border-border' : ''
                      } ${
                        isActive(link.href)
                          ? 'text-primary bg-primary/10' : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                      }`}
                    >
                      {link.label}
                    </a>
                  ))}
                </div>
              )}
            </div>

            {/* Logo — left on mobile, left on desktop */}
            <a
              href="/homepage"
              onClick={(e) => handleNavClick('/homepage', e)}
              className="flex items-center group cursor-pointer flex-1 md:flex-none justify-start md:justify-start md:order-1"
            >
              <div className="w-52 h-20 sm:w-72 sm:h-28 md:w-[40rem] md:h-56 flex items-center justify-start mt-[10px] md:mt-[10px] -ml-[calc(10%+39px)] md:-ml-[320px]">
                <AppImage
                  src="/assets/images/East_Coast_Sticker_Logo-1786121660508.png"
                  alt="East Coast Underground Logo"
                  className="w-full h-full object-contain"
                />
              </div>
            </a>

            {/* Mobile: Hamburger + Theme Toggle on the RIGHT (hidden on desktop) */}
            <div className="flex md:hidden items-center gap-1 relative z-10 flex-shrink-0 -mr-[10px]" ref={mobileMenuRef}>
              {/* Theme Toggle */}
              <button
                onClick={toggleTheme}
                className={`p-3 min-w-[44px] min-h-[44px] flex items-center justify-center rounded-lg transition-colors hover:bg-white/20 ${theme === 'light' ? 'text-gray-500' : 'text-white/80'}`}
                aria-label={theme === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}
              >
                {theme === 'light' ? (
                  <Icon name="MoonIcon" size={22} />
                ) : (
                  <Icon name="SunIcon" size={22} />
                )}
              </button>

              <button
                onClick={handleToggleMenu}
                className={`p-3 min-w-[44px] min-h-[44px] flex items-center justify-center rounded-lg transition-colors hover:bg-white/20 ${theme === 'light' ? 'text-gray-500' : 'text-white/80'}`}
                aria-label="Toggle menu"
                aria-expanded={menuOpen}
              >
                <Icon name={menuOpen ? 'XMarkIcon' : 'Bars3Icon'} size={26} />
              </button>

              {/* Dropdown Menu — anchored to right on mobile */}
              {menuOpen && (
                <div
                  onTouchStart={handleTouchStart}
                  onTouchEnd={handleTouchEnd}
                  className={`absolute top-full right-0 mt-2 w-64 bg-card border border-border rounded-xl shadow-2xl overflow-hidden ${
                    menuClosing ? 'animate-menu-slide-up' : 'animate-menu-slide-down'
                  }`}
                >
                  {navLinks.slice(0, 1).map((link, idx) => (
                    <a
                      key={link.href}
                      href={link.href}
                      onClick={(e) => handleNavClick(link.href, e)}
                      style={{ animationDelay: `${idx * 40}ms` }}
                      className={`animate-menu-item flex items-center gap-3 px-5 py-4 text-sm font-medium transition-colors border-b border-border cursor-pointer min-h-[52px] ${
                        isActive(link.href)
                          ? 'text-primary bg-primary/10' : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                      }`}
                    >
                      {link.label}
                    </a>
                  ))}

                  {/* Services with sub-items */}
                  <div style={{ animationDelay: '40ms' }} className="animate-menu-item">
                    <button
                      onClick={() => setServicesOpen(!servicesOpen)}
                      className={`w-full flex items-center justify-between px-5 py-4 text-sm font-medium transition-colors border-b border-border min-h-[52px] ${
                        isServicesActive
                          ? 'text-primary bg-primary/10' : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                      }`}
                    >
                      Services
                      <Icon
                        name="ChevronDownIcon"
                        size={14}
                        className={`transition-transform duration-200 ${servicesOpen ? 'rotate-180' : ''}`}
                      />
                    </button>
                    {servicesOpen && (
                      <div className="bg-muted/50 animate-services-expand">
                        {serviceLinks.map((link, idx) => (
                          <a
                            key={link.href}
                            href={link.href}
                            onClick={(e) => handleNavClick(link.href, e)}
                            style={{ animationDelay: `${idx * 30}ms` }}
                            className={`animate-menu-item flex items-center gap-3 pl-8 pr-5 py-3.5 text-sm transition-colors border-b border-border last:border-0 cursor-pointer min-h-[48px] ${
                              isActive(link.href)
                                ? 'text-primary bg-primary/10' : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                            }`}
                          >
                            <span className="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0" />
                            {link.label}
                          </a>
                        ))}
                      </div>
                    )}
                  </div>

                  {navLinks.slice(1).map((link, i) => (
                    <a
                      key={link.href}
                      href={link.href}
                      onClick={(e) => handleNavClick(link.href, e)}
                      style={{ animationDelay: `${(i + 2) * 40}ms` }}
                      className={`animate-menu-item flex items-center gap-3 px-5 py-4 text-sm font-medium transition-colors cursor-pointer min-h-[52px] ${
                        i < navLinks.slice(1).length - 1 ? 'border-b border-border' : ''
                      } ${
                        isActive(link.href)
                          ? 'text-primary bg-primary/10' : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                      }`}
                    >
                      {link.label}
                    </a>
                  ))}
                </div>
              )}
            </div>

          </div>
        </div>
      </header>
    </>
  );
}
